import { useMemo, useState } from "react";
import type { PieceTicket } from "@/api/types";
import { usePayrollRoster } from "@/hooks/usePayrollRoster";
import { usePieceTickets, usePieceTypeChoices } from "@/hooks/usePieceTickets";
import { formatMoney } from "@/lib/format";
import { PieceTicketDialog } from "./PieceTicketDialog";
import { PieceVoidDialog } from "./PieceVoidDialog";
import "./PieceTicketsPanel.css";

/**
 * ef-060-7 — the People tab's "Piece tickets" region (rendered by `H2a.tsx` with no props): OPEN
 * tickets by default (person, piece type, units, amount, period end, status), Edit and Void on open
 * ones only, a "Show paid and void" checkbox, and an "Add piece ticket" button. A LIST, never a second
 * `<table>` — the roster is the page's only table (people*.spec.ts locate it as such).
 *
 * `data-state` on the region reads `loading` / `error` / `empty` / `ready`.
 */

type DialogState = { kind: "add" } | { kind: "edit"; ticket: PieceTicket } | { kind: "void"; ticket: PieceTicket } | null;

const STATUS_LABEL: Record<PieceTicket["status"], string> = { open: "Open", paid: "Paid", void: "Void" };

const unitsFormatter = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });

export function PieceTicketsPanel() {
  const [showClosed, setShowClosed] = useState(false);
  const [dialog, setDialog] = useState<DialogState>(null);
  const { tickets, status, error, refetch } = usePieceTickets(showClosed);
  const roster = usePayrollRoster("tenant");
  const typeChoices = usePieceTypeChoices();

  // Active people only: a deactivated worker cannot be given new piece work.
  const people = useMemo(
    () => (roster.employees ?? []).filter((e) => e.active !== false).map((e) => ({ id: e.id, name: e.name })),
    [roster.employees],
  );

  function openAdd(): void {
    // The people and piece types may have changed on this very page since it loaded.
    roster.refetch();
    typeChoices.refetch();
    setDialog({ kind: "add" });
  }

  const state = status === "error" ? "error" : status === "loading" ? "loading" : (tickets ?? []).length === 0 ? "empty" : "ready";

  return (
    <section className="piece-tickets" aria-labelledby="piece-tickets-heading" data-state={state}>
      <div className="piece-tickets__head">
        <h2 id="piece-tickets-heading" className="piece-tickets__title">
          Piece tickets
        </h2>
        <button type="button" className="btn btn--primary" onClick={openAdd}>
          Add piece ticket
        </button>
      </div>

      <label className="piece-tickets__toggle">
        <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />
        Show paid and void
      </label>

      {status === "loading" && <p className="piece-tickets__note">Loading piece tickets…</p>}

      {status === "error" && (
        <p className="piece-tickets__error" role="alert">
          {error ?? "Could not load piece tickets."}{" "}
          <button type="button" className="btn btn--ghost" onClick={refetch}>
            Try again
          </button>
        </p>
      )}

      {status === "ready" && (tickets ?? []).length === 0 && (
        <p className="piece-tickets__note">
          {showClosed ? "No piece tickets yet." : "No open piece tickets. Add one when piece work is done."}
        </p>
      )}

      {status === "ready" && (tickets ?? []).length > 0 && (
        <ul className="piece-tickets__list" aria-label="Piece tickets">
          {(tickets ?? []).map((t) => {
            const nameId = `piece-ticket-${t.id}`;
            return (
              <li key={t.id} className="piece-tickets__row" data-state={t.status}>
                <div className="piece-tickets__who">
                  <span id={nameId} className="piece-tickets__person">
                    {t.employeeName}
                  </span>
                  <span className="piece-tickets__type">{t.pieceTypeName}</span>
                </div>
                <div className="piece-tickets__figures">
                  <span className="piece-tickets__units num">
                    {unitsFormatter.format(t.units)} {t.unit}
                  </span>
                  <span className="piece-tickets__amount num">{formatMoney(t.amount)}</span>
                </div>
                <div className="piece-tickets__when">
                  <span>Period ends {t.periodEnd}</span>
                  {t.source === "livestock" && t.asOf && <span>Head count on {t.asOf}</span>}
                  {t.status === "void" && t.voidReason && <span>Reason: {t.voidReason}</span>}
                  {t.note && <span>{t.note}</span>}
                </div>
                <span className="piece-tickets__status" data-status={t.status}>
                  {STATUS_LABEL[t.status]}
                </span>
                {t.status === "open" && (
                  <div className="piece-tickets__actions">
                    <button
                      type="button"
                      className="btn btn--ghost"
                      aria-describedby={nameId}
                      onClick={() => setDialog({ kind: "edit", ticket: t })}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost"
                      aria-describedby={nameId}
                      onClick={() => setDialog({ kind: "void", ticket: t })}
                    >
                      Void
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {dialog?.kind === "add" && (
        <PieceTicketDialog
          people={people}
          types={typeChoices.types ?? []}
          onClose={() => setDialog(null)}
          onSaved={refetch}
          onStale={refetch}
        />
      )}
      {dialog?.kind === "edit" && (
        <PieceTicketDialog
          key={dialog.ticket.id}
          ticket={dialog.ticket}
          people={people}
          types={typeChoices.types ?? []}
          onClose={() => setDialog(null)}
          onSaved={refetch}
          onStale={refetch}
        />
      )}
      {dialog?.kind === "void" && (
        <PieceVoidDialog
          key={dialog.ticket.id}
          ticket={dialog.ticket}
          onClose={() => setDialog(null)}
          onVoided={refetch}
          onStale={refetch}
        />
      )}
    </section>
  );
}
